// @ts-nocheck
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { lineEllipsisStyles } from "@store/Themes/common/mixins/typography";
import { autocompleteProps } from "@store/Themes/common/props/autocomplete";
import CardFooterComponent from "@store/Library/Widgets/Card/CardFooter";
import Button from "@store/MobiusOverrides/Button/Button";
import ProductContextAvailability from "@store/Overrides/Components/ProductContextAvailability";
import { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useMemo } from "react";
import { css } from "styled-components";

const StyledSpan = getStyledWrapper("span");
const StyledHr = getStyledWrapper("hr");

export interface BunzlAutoCompleteProductFooterStyles {
    cardFooter?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: InjectableCss;
    erpNumberText?: TypographyPresentationProps;
    productPartNumbers?: ProductPartNumbersStyles;
    horizontalRule?: InjectableCss;
    availabilityWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    selectOptionsButton?: InjectableCss;
    footerBottomWrapper?: InjectableCss;
}

interface BunzlAutoCompleteProductFooterProps {
    isAuthenticated: boolean;
    autocompleteProductItem: ProductAutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: BunzlAutoCompleteProductFooterStyles;
}

const BunzlAutoCompleteProductFooter: React.FC<BunzlAutoCompleteProductFooterProps> = ({
    isAuthenticated,
    autocompleteProductItem,
    goToUrl,
    extendedStyles,
}) => {
    const productContext = useProductContext();
    const { product, productInfo } = productContext;

    const bunzlAutoCompleteProductFooterStyles: BunzlAutoCompleteProductFooterStyles = useMemo(
        () => ({
            cardFooter: {
                css: css`
                    flex-direction: column;
                    justify-content: space-between;
                    ${!isAuthenticated && `margin-block: auto`};
                    padding: 0.25rem;
                    ${isAuthenticated ? `min-height: ${pxToRem(150)}` : ""};
                `,
            },
            footerBottomWrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                `,
            },
            infoWrapper: {
                css: css`
                    ${autocompleteProps.infoWrapper?.css};
                    width: 100%;
                    min-height: ${isAuthenticated ? pxToRem(88) : "100%"};
                    flex: unset;
                `,
            },
            title: {
                css: css`
                    font-weight: ${x => x.theme.design.typography.weight.bold};
                    ${lineEllipsisStyles(2)};
                    text-overflow: ellipsis;
                    cursor: pointer;
                    :hover {
                        text-decoration: underline;
                    }
                `,
            },
            erpNumberText: autocompleteProps.erpNumber,
            productPartNumbers: {
                wrapper: {
                    css: css`
                        display: flex;
                        flex-wrap: wrap;
                        margin-top: ${pxToRem(1)};
                        font-size: ${x => x.theme.design.typography.size.smallest};
                        color: inherit;
                        line-height: inherit;
                    `,
                },
                innerWrapper: {
                    css: css`
                        margin-bottom: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    `,
                },
            },
            horizontalRule: {
                css: css`
                    width: 100%;
                    color: ${getColor("common.border")};
                `,
            },
            availabilityWrapper: {
                css: css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: ${pxToRem(4)};
                    min-height: ${pxToRem(35)};
                `,
            },
            selectOptionsButton: {
                css: css`
                    height: 100%;
                    margin-top: 2px;
                `,
            },
            productPrice: {
                price: {
                    priceText: {
                        css: css`
                            font-size: ${pxToRem(16)};
                        `,
                    },
                },
                wrapper: {
                    css: css`
                        padding-top: 0;
                        max-width: 40%;
                        width: fit-content;
                        padding-bottom: 0;
                    `,
                },
            },
        }),
        [isAuthenticated],
    );

    const styles = useMergeProps(bunzlAutoCompleteProductFooterStyles, extendedStyles);

    return (
        <CardFooterComponent extendedStyles={styles.cardFooter}>
            <StyledSpan css={styles.infoWrapper?.css} className="autocomplete-products-info">
                <Typography
                    css={styles.title?.css}
                    onClick={() => goToUrl(autocompleteProductItem.url)}
                    data-test-selector={`productId_${autocompleteProductItem.id}`}
                    className="autocomplete-products-title"
                >
                    {autocompleteProductItem.displayTitle}
                </Typography>
                <Typography
                    {...styles.erpNumberText}
                    data-test-selector="autocompleteProducts_PartNumber"
                    className="autocomplete-products-erp-number"
                >
                    <ProductPartNumbers
                        productNumber={autocompleteProductItem.displayErpNumber}
                        customerProductNumber={autocompleteProductItem.displayName}
                        manufacturerItem={autocompleteProductItem.displayManufacturerItemNumber}
                        showCustomerName={!!autocompleteProductItem.isNameCustomerOverride}
                        extendedStyles={styles.productPartNumbers}
                    />
                </Typography>
            </StyledSpan>

            {isAuthenticated && (
                <StyledWrapper {...styles.footerBottomWrapper}>
                    <StyledHr css={styles.horizontalRule?.css} />
                    {productContext && !product?.isVariantParent && (
                        <StyledWrapper css={styles.availabilityWrapper?.css}>
                            <ProductPrice
                                product={productContext}
                                extendedStyles={styles.productPrice}
                                showLabel={false}
                            />
                            <StyledWrapper>
                                {productContext.productInfo && <ProductContextAvailability />}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}

                    {productInfo && product?.isVariantParent && (
                        <Button
                            sizeVariant="small"
                            onClick={() => goToUrl(autocompleteProductItem.url)}
                            {...styles.selectOptionsButton}
                        >
                            {translate("Select options")}
                        </Button>
                    )}
                </StyledWrapper>
            )}
        </CardFooterComponent>
    );
};

export default BunzlAutoCompleteProductFooter;
